import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { GeneralSetting } from 'src/model/GeneralSetting';
import { TimeParameter } from 'src/model/enum/parameterEnums';
import { generalSettingsActions } from 'src/store/slices/generalSettingsSlice';
import { RootState } from 'src/store/store';
import useUser from './useUser';

export const useGeneralSettings = () => {
    const dispatch = useDispatch();
    const ac = bindActionCreators(generalSettingsActions, dispatch);
    const { generalSettings } = useSelector((state: RootState) => state.generalSettings);
    const { isProUser } = useUser();

    useEffect(() => {
        ac.getSettings();
    }, [ac]);

    const updateGeneralSettings = useCallback((settings: Partial<GeneralSetting>) => {
        const updatedSettings = { ...generalSettings, ...settings };
        ac.setGeneralSettings(updatedSettings);
    }, [ac, generalSettings]);

    const handleLogout = useCallback(() => {
        ac.logout();
    }, [ac]);

    const incrementUncoveredWordCount = useCallback(() => {

        const currentCount = generalSettings.UncoverWordCount;
        const newCount = currentCount + 1;
        updateGeneralSettings({ UncoverWordCount: newCount });
    }, [updateGeneralSettings, generalSettings]);

    const incrementRangePopUpCount = useCallback(() => {
        const currentCount = generalSettings.RangePopUpCount || 0;
        const newCount = currentCount + 1;
        updateGeneralSettings({ RangePopUpCount: newCount });
    }, [updateGeneralSettings, generalSettings]);

    const checkWritingFeedbackLimit = useCallback((updateCount: boolean = true) => {
        if (isProUser()) return true;
        const currentTime = Date.now();
        const lastResetDate = generalSettings.LastWritingFeedbackResetDate ? parseInt(generalSettings.LastWritingFeedbackResetDate) : 0;
        const currentCount = generalSettings.WritingFeedbackCount || 0;
        const oneDayInMs = TimeParameter.OneDay;

        // If it's a new day, reset the count
        if (lastResetDate === 0 || currentTime - lastResetDate >= oneDayInMs) {
            if (updateCount) {
                updateGeneralSettings({
                    LastWritingFeedbackResetDate: currentTime.toString(),
                    WritingFeedbackCount: 1
                });
            }
            return true;
        }

        // Check if user has used less than 2 writing feedbacks today
        if (currentCount < 2) {
            if (updateCount) {
                updateGeneralSettings({
                    WritingFeedbackCount: currentCount + 1
                });
            }
            return true;
        }

        return false;
    }, [generalSettings, updateGeneralSettings, isProUser]);

    const checkRatingPopUpLimit = useCallback((updateDate: boolean = true) => {
        const currentTime = Date.now();
        const lastShowDate = generalSettings.LastRatingPopUpShowDate ? parseInt(generalSettings.LastRatingPopUpShowDate) : 0;
        const oneDayInMs = TimeParameter.OneDay;

        // If it's a new day or never shown before, allow showing
        if (lastShowDate === 0 || currentTime - lastShowDate >= oneDayInMs) {
            if (updateDate) {
                updateGeneralSettings({
                    LastRatingPopUpShowDate: currentTime.toString()
                });
            }
            return true;
        }

        return false;
    }, [generalSettings, updateGeneralSettings]);

    return {
        generalSettings,
        updateGeneralSettings,
        handleLogout,
        incrementUncoveredWordCount,
        incrementRangePopUpCount,
        checkWritingFeedbackLimit,
        checkRatingPopUpLimit
    };
}; 